import { Model } from '@/clients/http'

import type { AnalyticsDataName, AnalyticsSignal } from '@/types/analytics'

export class AnalyticsModel extends Model {
  public static readonly correlations = {
    DATA: 'GET_ANALYTICS_DATA',
    SIGNALS: 'GET_ANALYTICS_SIGNALS',
    UPDATE_SIGNAL: 'UPDATE_ANALYTICS_SIGNAL',
  }

  public static readonly endpoints = {
    data: (name: AnalyticsDataName, query: Record<string, unknown>) => this.createEndpoint({ url: `/api/backoffice/analytics/${name}`, query }),
    signals: (query: Record<string, unknown>) => this.createEndpoint({ url: '/api/backoffice/analytics/signals', query }),
    signal: (id: string) => this.createEndpoint({ url: `/api/backoffice/analytics/signals/${id}` }),
  }

  /** One storefront analytics view; `result` holds it (products also return `count`). */
  public static readonly getData = async <T,>(name: AnalyticsDataName, query: Record<string, unknown>) =>
    this.createHttpRequest<{ result: T; count?: number }, { message?: string }>({
      correlationKey: this.correlations.DATA,
      payload: { method: 'get', url: this.endpoints.data(name, query), useBearerToken: true },
    })

  /** Current signals, with how many days of data the store has (signals need a few weeks). */
  public static readonly getSignals = async (query: Record<string, unknown> = {}) =>
    this.createHttpRequest<{ result: AnalyticsSignal[]; history_days: number; min_history_days: number }, { message?: string }>({
      correlationKey: this.correlations.SIGNALS,
      payload: { method: 'get', url: this.endpoints.signals(query), useBearerToken: true },
    })

  /** Marks a signal seen or dismissed, and/or records whether it was useful. */
  public static readonly updateSignal = async (id: string, body: { status?: AnalyticsSignal['status']; feedback?: AnalyticsSignal['feedback'] }) =>
    this.createHttpRequest<{ result: AnalyticsSignal }, { message?: string }>({
      correlationKey: this.correlations.UPDATE_SIGNAL,
      payload: { method: 'patch', url: this.endpoints.signal(id), body, useBearerToken: true },
    })
}
